<template>
  <div id="shipper-accounts-receivable">
    <BreadCrumb :items="breadcrumbItems" />
    <v-card elevation="1" class="pa-2">
      <v-card-title class="header d-flex justify-space-between align-center">
        <h1 class="pa-0">Embarcador a receber</h1>
        <div>
          <v-btn
            elevation="2"
            color="success"
            large
            :loading="isOpenLoading"
            :disabled="selectedCheckbox.length == 0"
            @click="openDialog()"
          >
            CRIAR FATURA DE RECEBIMENTO
          </v-btn>
        </div>
      </v-card-title>

      <v-expansion-panels class="px-5 py-1 mt-2" accordion>
        <v-expansion-panel class="elevation-1">
          <v-expansion-panel-header color="#f2f2f2" class="px-4">
            <span>Filtrar por</span>
            <template v-slot:actions>
              <v-icon color="black"> mdi-chevron-down </v-icon>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row class="mt-2 pb-1">
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="senderId"
                  label="Embarcador"
                  :items="senders"
                  item-text="name"
                  item-value="id"
                  class="auto-complete"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="hasReceipt" label="Possui Canhoto" :items="hasReceiptOptions" clearable></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox
                  v-model="boardingNumber"
                  :search-input.sync="boardingNumberInput"
                  append-icon=""
                  label="Número externo da Carga"
                  hide-selected
                  multiple
                  chips
                  deletable-chips
                  @keydown.native="filterInput"
                  @change="saveBoardingNumbers"
                ></v-combobox>
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox
                  v-model="numberCte"
                  :search-input.sync="cteNumberInput"
                  append-icon=""
                  label="Número CTE"
                  hide-selected
                  multiple
                  chips
                  deletable-chips
                  @keydown.native="filterInput"
                  @change="saveCteNumber"
                ></v-combobox>
              </v-col>
              <v-col cols="12" md="4">
                <v-combobox
                  v-model="numberNfe"
                  :search-input.sync="numberNfeInput"
                  append-icon=""
                  label="Número da Nota Fiscal"
                  hide-selected
                  multiple
                  chips
                  deletable-chips
                  @keydown.native="filterInput"
                  @change="saveNfeNumber"
                ></v-combobox>
              </v-col>

              <v-col cols="12" class="d-flex justify-end align-center">
                <v-btn
                  elevation="2"
                  color="red"
                  class="mx-2"
                  :disabled="!hasFilters"
                  :dark="hasFilters"
                  @click="cleanFilter()"
                >
                  Limpar filtros
                </v-btn>
                <v-btn
                  :loading="isLoading"
                  elevation="2"
                  color="green"
                  :disabled="!hasFilters"
                  :dark="hasFilters"
                  @click="search()"
                >
                  Buscar
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="table mt-10 p-relative">
        <table-title-row :columnsOptions="columnsOptions"></table-title-row>
        <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>
        <div v-if="nodes.length != 0">
          <template v-for="(node, nodeIndex) in nodes">
            <div :key="node.data.id">
              <table-body-row
                :checkboxDisabled="
                  (!node.hasSelectedChildren && selectedCheckbox.length > 0) || !node.hasFinishDelivery
                "
                :node="node"
                :nodeIndex="nodeIndex"
                :columns="columns"
                :lengthDataToTruncate="50"
                type="Shipper"
                :enableCheckbox="true"
                :selectedCheckbox="selectedCheckbox"
                @changeCheckbox="changeSelectedCheckbox"
                @onExpand="onExpand(node)"
                @click="setShipperSelected(node.data.id)"
              >
              </table-body-row>
              <v-row v-if="node.open" style="margin-top: 0">
                <v-progress-linear
                  v-if="loadingShipperKey === nodeIndex && isShipperLoading"
                  class="mx-3"
                  indeterminate
                  color="primary"
                ></v-progress-linear>

                <v-col cols="12" class="mb-4">
                  <v-card>
                    <v-card-text class="p-5">
                      <table-title-row :columnsOptions="columnsCargoOptions"> </table-title-row>
                      <template v-for="(cargo, cargoIndex) in node.children">
                        <div :key="cargo.data.id">
                          <table-body-row
                            :checkboxDisabled="
                              (!node.hasSelectedChildren && selectedCheckbox.length > 0) || !cargo.hasFinishDelivery
                            "
                            :node="cargo"
                            :nodeIndex="cargoIndex"
                            :columns="columnsCargo"
                            :lengthDataToTruncate="15"
                            type="AccountsReceivableShipperCargo"
                            :enableCheckbox="cargo.enableCheckboxCargo"
                            :selectedCheckbox="selectedCheckbox"
                            @changeCheckbox="changeSelectedCheckbox"
                            @onExpand="onExpandCargo(cargo)"
                            @click="setShipperSelected(node.data.id)"
                          >
                          </table-body-row>
                          <v-row v-if="cargo.open" style="margin-top: 0">
                            <v-progress-linear
                              v-if="loadingCargoKey === cargoIndex && isCargoLoading"
                              class="mx-3"
                              indeterminate
                              color="primary"
                            ></v-progress-linear>

                            <v-col cols="12" class="mb-4">
                              <v-card>
                                <v-card-text class="p-5">
                                  <table-title-row :columnsOptions="columnsDeliveriesOptions"></table-title-row>
                                  <template v-for="(delivery, deliveryIndex) in cargo.children">
                                    <div :key="delivery.data.id">
                                      <table-body-row
                                        :checkboxDisabled="
                                          (!node.hasSelectedChildren && selectedCheckbox.length > 0) ||
                                          !delivery.isFinishDelivery
                                        "
                                        :node="delivery"
                                        :nodeIndex="deliveryIndex"
                                        :columns="columnsDeliveries"
                                        :lengthDataToTruncate="30"
                                        type="AccountsReceivableShipperDelivery"
                                        enableCheckbox
                                        :selectedCheckbox="selectedCheckbox"
                                        :enableDropdown="true"
                                        :itemsDropdown="itemsDropdownDelivery"
                                        @actionsDropdown="actionsDropdownDelivery"
                                        @changeCheckbox="changeSelectedCheckbox"
                                        @onExpand="onExpandDelivery(delivery)"
                                        @click="setShipperSelected(node.data.id)"
                                      >
                                        <template #costFreight="{ item }">{{ transformPrice(item) }}</template>
                                      </table-body-row>

                                      <Status :nodeIndex="deliveryIndex" :status="delivery.data.deliveryStatus" />

                                      <v-row v-if="delivery.open" style="margin-top: 0">
                                        <v-progress-linear
                                          v-if="loadingDeliveryKey === deliveryIndex && isDeliveryLoading"
                                          class="mx-3"
                                          indeterminate
                                          color="primary"
                                        ></v-progress-linear>

                                        <v-col cols="12" class="mb-4">
                                          <v-card>
                                            <v-card-text class="p-5">
                                              <table-title-row :columnsOptions="columnsNfesOptions"> </table-title-row>
                                              <template v-for="(nfe, nfeIndex) in delivery.children">
                                                <div :key="nfe.data.id">
                                                  <table-body-row
                                                    :node="nfe"
                                                    :nodeIndex="nfeIndex"
                                                    :columns="columnsNfes"
                                                    :lengthDataToTruncate="30"
                                                    type="AccountsReceivableShipperNfe"
                                                    :enableDropdown="true"
                                                    :itemsDropdown="itemsDropdownNfe"
                                                    @actionsDropdown="actionsDropdownNfe"
                                                    @receiptFlagClick="handleRedirectToReceiptsManagement(nfe.data)"
                                                  >
                                                  </table-body-row>

                                                  <Status :nodeIndex="nfeIndex" :status="nfe.data.nfeStatus" />
                                                </div>
                                              </template>
                                            </v-card-text>
                                          </v-card>
                                        </v-col>
                                      </v-row>
                                    </div>
                                  </template>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </template>
        </div>
        <div class="table mt-10 p-relative" v-else>Nenhum resultado encontrado</div>
        <div class="mt50"></div>
        <div class="d-flex justify-space-between">
          <v-pagination
            v-model="pageNumber"
            :length="totalPages"
            :total-visible="7"
            @input="onPageChange"
          ></v-pagination>
          <items-per-page-selector @change="onPageSizeChange" class="pl-5"></items-per-page-selector>
        </div>
      </div>
    </v-card>
    <ReceivePayment
      :isOpen.sync="isOpenDialog"
      :selectedFather="selectedShipper.data"
      :deliveryAllItems="deliveryAllItems"
      :selectedItems="selectedDeliveries"
      @shouldUpdateList="fetchAllAccountsReceivableShipper"
    />

    <DeliveryEditObservation
      :isOpen.sync="isOpenObservationDelivery"
      :deliveryToEditObservation="deliveryToEditObservation"
      @shouldUpdateList="fetchAllAccountsReceivableShipper"
    />

    <HistoricNfe :id="nfeSelectedId" :open="isOpenHistoryNfe" @closeHistoricNfe="isOpenHistoryNfe = false" />

    <MessagesError :errors="errors" :type="errorsType" :error500="error500" @closeMessages="errors = []" />
  </div>
</template>

<script>
import BreadCrumb from '@/components/BreadCrumb.vue';
import { mapActions, mapMutations } from 'vuex';
import TableTitleRow from '@/components/Table/Table-Title-Row.vue';
import TableBodyRow from '@/components/Table/Table-Body-Row.vue';
import maskPrice from '@/utils/maskPrice';
import maskData from '@/utils/maskData';
import changeCheckbox from '@/utils/changeSelectedCheckbox';
import Status from '@/components/Table/Status.vue';
import ReceivePayment from '@/components/AccountsReceivable/ReceivePayment.vue';
import HistoricNfe from '@/components/Nfe/HistoricNfe.vue';
import ItemsPerPageSelector from '@/components/ItemsPerPageSelector.vue';
import DeliveryEditObservation from '@/components/AccountsReceivable/DeliveryEditObservationDialog.vue';
import MessagesError from '@/components/MessagesError.vue';
import comboboxFormatter from '@/utils/comboboxFormatter';
import allowedChars from '@/utils/allowedChars';

export default {
  name: 'ShippersAccountsReceivable',
  components: {
    BreadCrumb,
    TableTitleRow,
    TableBodyRow,
    ReceivePayment,
    HistoricNfe,
    Status,
    MessagesError,
    ItemsPerPageSelector,
    DeliveryEditObservation
  },
  data: () => ({
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Financeiro',
        disabled: true
      },
      {
        text: 'Embarcador a receber',
        disabled: true
      }
    ],
    isLoading: false,
    isOpenLoading: false,
    isOpenDialog: false,
    loadingShipperKey: null,
    isShipperLoading: false,
    loadingCargoKey: null,
    isCargoLoading: false,
    loadingDeliveryKey: null,
    isDeliveryLoading: false,
    isOpenHistoryNfe: false,
    isOpenObservationDelivery: false,
    nfeSelectedId: null,
    hasReceipt: null,
    shipperSelected: '',
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: null,
    senderId: null,
    shipperId: null,
    senders: [],
    boardingNumberInput: '',
    cteNumberInput: '',
    numberNfeInput: '',
    numberCte: [],
    numberNfe: [],
    boardingNumber: [],
    selectedCheckbox: [],
    selectedDeliveries: [],
    deliveryAllItems: [],
    selectedShipper: {
      children: [],
      data: { name: '' }
    },
    nodes: [],
    columns: ['name', 'costFreight', 'actions'],
    columnsOptions: [
      {
        value: 'senderName',
        text: 'NOME DO EMBARCADOR'
      },
      {
        value: 'costFreight',
        text: 'VALOR A RECEBER'
      },
      {
        value: 'actions',
        text: 'Ações'
      }
    ],
    columnsCargo: [
      'type',
      'amountToReceive',
      'number',
      'transhipmentName',
      'openDaysCounter',
      'progress',
      'progressPaid',
      'actions'
    ],
    columnsCargoOptions: [
      {
        value: 'type',
        text: 'TIPO'
      },
      {
        value: 'amountToReceive',
        text: 'VALOR A RECEBER'
      },
      {
        value: 'number',
        text: 'EMBARQUE EXTERNO'
      },
      {
        value: 'transhipmentName',
        text: 'TRANSBORDO'
      },
      {
        value: 'openDaysCounter',
        text: 'DIAS EM ABERTO'
      },
      {
        value: 'progress',
        text: 'CONCLUSÃO'
      },
      {
        value: 'progressPaid',
        text: 'PAGOS'
      },
      {
        value: 'actions',
        text: 'AÇÕES'
      }
    ],
    columnsDeliveries: ['type', 'numberCte', 'authorizationDate', 'costFreight', 'amountNfe', 'actions'],
    columnsDeliveriesOptions: [
      {
        value: 'type',
        text: 'TIPO'
      },
      {
        value: 'numberCte',
        text: 'NÚMERO CTE'
      },
      {
        value: 'authorizationDate',
        text: 'DATA DE AUTORIZAÇÃO'
      },
      {
        value: 'costFreight',
        text: 'CUSTO DO FRETE'
      },
      {
        value: 'amountNfe',
        text: 'QUANTIDADE NFE'
      },
      {
        value: 'actions',
        text: 'AÇÕES'
      }
    ],
    columnsNfes: ['type', 'number', 'price', 'completionDate', 'canhotoType', 'actions'],
    columnsNfesOptions: [
      {
        value: 'type',
        text: 'TIPO'
      },
      {
        value: 'number',
        text: 'NÚMERO NFE'
      },
      {
        value: 'price',
        text: 'PREÇO DA NF'
      },
      {
        value: 'completionDate',
        text: 'DATA DE CONCLUSÃO'
      },
      {
        value: 'canhotoType',
        text: 'STATUS DO CANHOTO'
      },
      {
        value: 'actions',
        text: 'Ações'
      }
    ],
    itemsDropdownDelivery: [{ title: 'Editar Observação' }],
    itemsDropdownNfe: [{ title: 'Histórico' }],
    hasReceiptOptions: [
      { text: 'Sim', value: true },
      { text: 'Não', value: false }
    ],
    deliveryToEditObservation: {
      id: null,
      observation: null
    },
    data: [],
    errors: [],
    errorsType: 'error',
    error500: null,
    message: null
  }),

  computed: {
    hasFilters() {
      if (
        this.senderId ||
        this.hasReceipt !== null ||
        this.numberCte.length !== 0 ||
        this.boardingNumber.length !== 0 ||
        this.numberNfe.length !== 0
      ) {
        return true;
      }
      return false;
    }
  },

  created() {
    this.fetchAllAccountsReceivableShipper();
    this.getAllSenders();
  },

  methods: {
    ...mapActions('AccountsReceivableStore', ['getSenders']),
    ...mapActions('ShippersReceivableStore', [
      'fetchShippersWithoutInvoice',
      'fetchCargosByShipperId',
      'fetchAllDeliveriesByCargoId',
      'fetchDeliveriesByCargoId',
      'fetchNfesByDeliveryId',
      'getAllDeliveriesWithoutInvoicesByShipperId',
      'getDeliveriesWithoutInvoicesByShipperId'
    ]),
    ...mapMutations('NfeStore', ['setSelectedCheckbox']),

    async openDialog() {
      this.selectedDeliveries = [];
      this.data = [];
      this.isOpenLoading = true;

      const filters = this.getFilters();
      filters.shipperId = this.shipperSelected;

      const deliveriesByShipper = await this.fetchAllDeliveriesByShipper(this.shipperSelected);
      this.deliveryAllItems = this.mapAllFatherAllItems(deliveriesByShipper);

      if (this.hasShippersSelected(this.selectedCheckbox) && !this.hasFilters) {
        this.selectedDeliveries = deliveriesByShipper;
      } else if (this.hasShippersSelected(this.selectedCheckbox) && this.hasFilters) {
        this.data = await this.getDeliveriesWithoutInvoicesByShipperId({ ...filters });
        this.selectedDeliveries = this.data;
      } else if (this.hasCargoSelected(this.selectedCheckbox)) {
        this.selectedDeliveries = this.data;
        const cargoIds = this.selectedCheckbox
          .filter((selected) => {
            if (selected.type === 'Carga') return selected;
          })
          .map((selectedFiltered) => selectedFiltered.id);

        let deliveries = [];

        await Promise.all(
          cargoIds.map(async (cargoId) => {
            filters.cargoId = cargoId;
            const data = await this.fetchDeliveriesByCargoId({ ...filters });
            deliveries = [...deliveries, ...data];
          })
        );

        if (this.hasDeliveriesSelected(this.selectedCheckbox)) {
          const deliveriessFiltered = this.selectedCheckbox
            .filter((selected) => selected.type === 'Entrega')
            .filter((delivery) => {
              const deliverysIds = deliveries.map((delivery) => delivery.id);
              if (!deliverysIds.includes(delivery.id) && delivery.finishedDeliveriesWithoutInvoice) {
                return delivery;
              }
            });

          deliveries = [...new Set([...deliveries, ...deliveriessFiltered])];
        }

        this.selectedDeliveries = deliveries;
      } else {
        this.selectedDeliveries = this.selectedCheckbox.filter((selected) => selected.type === 'Entrega');
      }

      this.isOpenDialog = true;
      this.isOpenLoading = false;
    },

    mapAllFatherAllItems(deliveries) {
      return deliveries.map((item, index) => {
        return {
          key: index,
          data: {
            ...item
          },
          selected: false,
          type: 'AccountsPayableTranshipmentStop'
        };
      });
    },

    async fetchAllAccountsReceivableShipper() {
      try {
        this.isLoading = true;

        const filters = this.getFilters();
        const { data, totalPages, totalRecords } = await this.fetchShippersWithoutInvoice({
          ...filters,
          pageSize: this.pageSize,
          pageNumber: this.pageNumber
        });

        this.totalRecords = totalRecords;
        this.totalPages = totalPages;

        const shippers = data.map((item, index) => {
          return {
            key: index,
            data: {
              ...item,
              costFreight: this.transformPrice(item.costFreight),
              type: 'Shipper'
            },
            children: [],
            type: 'AccountsReceivableShipperFather',
            open: false,
            selected: false,
            hasSelectedChildren: false,
            hasFinishDelivery: !!item.finishedDeliveriesWithoutInvoice
          };
        });

        this.nodes = shippers;
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
        setTimeout(() => {
          this.selectedCheckbox = [];
        }, 0);
      }
    },

    async fetchCargosByIdShipper(selected) {
      try {
        this.isShipperLoading = true;

        const filters = this.getFilters();
        this.shipperId = selected.data.id;
        filters.shipperId = this.shipperId;

        const data = await this.fetchCargosByShipperId({ ...filters });
        const newCargos = data.map((item, index) => {
          const percentageDeliveryFinished = (item.finishedDeliveries / item.totalDeliveries) * 100;
          const percentagePaidFinished = (item.totalPaid / item.finishedDeliveries) * 100;
          return {
            key: selected.key + '-' + index,
            data: {
              ...item,
              percentageDeliveryFinished,
              percentagePaidFinished,
              amountToReceive: this.transformPrice(item.amountToReceive),
              type: 'Carga'
            },
            selected: false,
            type: 'AccountsReceivableShipperCargo',
            children: [],
            open: false,
            hasSelectedChildren: false,
            hasFinishDelivery: item.finishedDeliveriesWithoutInvoice,
            enableCheckboxCargo: true
          };
        });

        this.nodes[selected.key].children = newCargos;
        if (selected.selected) {
          this.nodes[selected.key].children.map((item) => {
            item.selected = true;
            if (!this.selectedCheckbox.includes(item.data)) {
              this.selectedCheckbox.push(item.data);
            }
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isShipperLoading = false;
        this.loading = false;
      }
    },

    async fetchAllDeliveriesByCargo(selected) {
      const idNode = Number(selected.key.split('-')[0]);
      const idCargo = Number(selected.key.split('-')[1]);
      try {
        this.isCargoLoading = true;

        const filters = this.getFilters();
        filters.cargoId = selected.data.id;
        filters.shipperId = this.shipperId;

        const data = await this.fetchAllDeliveriesByCargoId({ ...filters });
        const newDeliveries = data.map((item, index) => {
          return {
            key: idNode + '-' + idCargo + '-' + index,
            data: {
              ...item,
              authorizationDate: this.transformDate(item.authorizationDate),
              type: 'Entrega'
            },
            selected: false,
            type: 'AccountsReceivableShipperDelivery',
            children: [],
            open: false,
            hasSelectedChildren: false,
            isFinishDelivery: item.finishedDeliveriesWithoutInvoice
          };
        });
        this.nodes[idNode].children[idCargo].children = newDeliveries;

        if (selected.selected) {
          this.nodes[idNode].children[idCargo].children.map((item) => {
            item.selected = true;
            if (!this.selectedCheckbox.includes(item.data)) {
              this.selectedCheckbox.push(item.data);
            }
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isCargoLoading = false;
        this.loading = false;
      }
    },

    async fetchAllDeliveriesByShipper(selected) {
      try {
        const deliveriesByShipper = await this.getAllDeliveriesWithoutInvoicesByShipperId(selected);
        return deliveriesByShipper;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchNfesByIdDelivery(selected) {
      const idNode = Number(selected.key.split('-')[0]);
      const idCargo = Number(selected.key.split('-')[1]);
      const idDelivieres = Number(selected.key.split('-')[2]);
      try {
        this.isDeliveryLoading = true;

        const filters = this.getFilters();
        filters.deliveryId = selected.data.id;

        const data = await this.fetchNfesByDeliveryId({ ...filters });
        const newNfes = data.map((item, index) => {
          return {
            key: idNode + '-' + idCargo + '-' + idDelivieres + '-' + index,
            data: {
              ...item,
              completionDate: this.transformDate(item.completionDate),
              price: this.transformPrice(item.price),
              canhotoType: item.missingReceipt ? '1' : !item.receiptValidated ? '2' : '3',
              type: 'Nota Fiscal'
            },
            selected: false,
            type: 'AccountsReceivableShipperNfe'
          };
        });

        this.nodes[idNode].children[idCargo].children[idDelivieres].children = newNfes;
      } catch (err) {
        console.error(err);
      } finally {
        this.isDeliveryLoading = false;
        this.loading = false;
      }
    },

    async getAllSenders() {
      try {
        this.senders = await this.getSenders();
      } catch (error) {
        console.error(error);
      }
    },

    async onExpand(selected) {
      this.loadingShipperKey = selected.key;
      this.nodes[selected.key].open = !this.nodes[selected.key].open;
      if (selected.children.length === 0) {
        await this.fetchCargosByIdShipper(selected);
      }
      if (selected.selected) {
        selected.children.forEach((cargo) => {
          if (!cargo.hasFinishDelivery) {
            cargo.selected = false;
            this.selectedCheckbox = this.selectedCheckbox.filter((item) => item.id !== cargo.data.id);
          }
        });
      }
    },

    async onExpandCargo(selected) {
      const idNode = Number(selected.key.split('-')[0]);
      const idCargo = Number(selected.key.split('-')[1]);

      this.loadingCargoKey = idCargo;

      this.nodes[idNode].children[idCargo].open = !this.nodes[idNode].children[idCargo].open;

      if (selected.children.length === 0) {
        await this.fetchAllDeliveriesByCargo(selected);
      }

      if (selected.selected) {
        selected.children.forEach((delivery) => {
          if (!delivery.isFinishDelivery) {
            delivery.selected = false;
            this.selectedCheckbox = this.selectedCheckbox.filter((item) => item.id !== delivery.data.id);
          }
        });
      }
    },

    async onExpandDelivery(selected) {
      const idNode = Number(selected.key.split('-')[0]);
      const idCargo = Number(selected.key.split('-')[1]);
      const idDelivery = Number(selected.key.split('-')[2]);

      this.loadingDeliveryKey = idDelivery;

      this.nodes[idNode].children[idCargo].children[idDelivery].open =
        !this.nodes[idNode].children[idCargo].children[idDelivery].open;

      if (selected.children.length === 0) {
        await this.fetchNfesByIdDelivery(selected);
      }
    },

    actionsDropdownNfe(index, node) {
      this.nfeSelectedId = node.data.id;
      if (index === 0) {
        this.isOpenHistoryNfe = true;
      }
    },

    actionsDropdownDelivery(index, node) {
      this.deliveryToEditObservation = {
        id: node.data.id,
        observation: node.data.observation
      };
      if (index === 0) {
        this.isOpenObservationDelivery = true;
      }
    },

    changeSelectedCheckbox(item, children) {
      if (item.type === 'AccountsReceivableShipperCargo') {
        const courierKey = item.key.split('-')[0];
        const cargoKey = item.key.split('-')[1];
        this.selectedShipper = this.nodes[courierKey];

        this.selectedCheckbox = changeCheckbox(this.selectedCheckbox, item, children, 0, 0, 0, this.nodes[courierKey]);
        this.selectedShipper.children[cargoKey].children.forEach((delivery) => {
          if (!delivery.isFinishDelivery) {
            delivery.selected = false;
            this.selectedCheckbox = this.selectedCheckbox.filter((item) => item.id !== delivery.data.id);
          }
        });
      } else if (item.type === 'AccountsReceivableShipperDelivery') {
        const courierKey = item.key.split('-')[0];
        const cargoKey = item.key.split('-')[1];
        this.selectedShipper = this.nodes[courierKey];

        this.selectedCheckbox = changeCheckbox(
          this.selectedCheckbox,
          item,
          children,
          cargoKey,
          0,
          0,
          this.nodes[courierKey]
        );
      } else {
        this.selectedShipper = this.nodes[item.key];
        this.selectedCheckbox = changeCheckbox(this.selectedCheckbox, item, children);
        this.selectedShipper.children.forEach((cargo) => {
          if (!cargo.hasFinishDelivery) {
            cargo.selected = false;
            this.selectedCheckbox = this.selectedCheckbox.filter((item) => item.id !== cargo.data.id);
          }
        });
      }
    },

    setShipperSelected(shippertId) {
      this.shipperSelected = shippertId;
    },

    handleRedirectToReceiptsManagement(nfe) {
      this.setSelectedCheckbox([nfe]);
      window.open('#/shipments/receipts', '_blank');
    },

    hasShippersSelected(array) {
      return array.some((array) => array.type === 'Shipper');
    },
    hasCargoSelected(array) {
      return array.some((array) => array.type === 'Carga');
    },
    hasDeliveriesSelected(array) {
      return array.some((array) => array.type === 'Entrega');
    },

    transformPrice(price) {
      return maskPrice(price);
    },

    transformDate(date) {
      return maskData(date);
    },

    async onPageChange(page) {
      this.pageNumber = page;
      await this.fetchAllAccountsReceivableShipper();
    },

    async onPageSizeChange(value) {
      this.pageSize = value;
      this.pageNumber = 1;
      await this.fetchAllAccountsReceivableShipper();
    },

    search() {
      this.pageNumber = 1;
      this.fetchAllAccountsReceivableShipper();
    },

    setClearErrorTimer() {
      setTimeout(() => {
        this.errors = [];
      }, 5000);
    },

    filterInput(event) {
      return allowedChars(event, /[0-9,.;/]/);
    },

    saveBoardingNumbers(value) {
      this.boardingNumber = comboboxFormatter(value);
    },

    saveCteNumber(value) {
      this.numberCte = comboboxFormatter(value);
    },

    saveNfeNumber(value) {
      this.numberNfe = comboboxFormatter(value);
    },

    cleanFilter() {
      this.senderId = null;
      this.hasReceipt = null;
      this.numberCte = [];
      this.boardingNumber = [];
      this.numberNfe = [];

      this.fetchAllAccountsReceivableShipper();
    },

    getFilters() {
      return {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        shipperId: null,
        cargoId: null,
        deliveryId: null,
        senderId: this.senderId,
        hasReceipt: this.hasReceipt,
        boardingNumber: this.boardingNumber,
        numberCte: this.numberCte,
        numberNfe: this.numberNfe
      };
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.v-select.v-select--chips .v-select__selections {
  min-height: 50px;
}

::v-deep .v-input__append-inner.v-input__append-inner .v-input__icon--clear {
  padding-top: 17px;
}
</style>